/**
 * HYPSEYA — Practitioner Portal
 * Therapist / coach B2B dashboard.
 * Access via: /practitioner?token=<practitioner_token>
 * or after practitioner login (future auth flow).
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { User, Chart, Wave, ArrowLeft, ArrowUp, ArrowDown, Alert } from '@/components/HypIcons';
import orbUrl from '../orbAsset';

const API = process.env.REACT_APP_BACKEND_URL + '/api';


const wordmarkUrl = '/hypseya-wordmark.png';

// ── Client card ────────────────────────────────────────────────────────────
function ClientCard({ client, onSelect, isSelected }) {
  const eri     = client.eri_score;
  const band    = eri >= 80 ? 'strong' : eri >= 60 ? 'growing' : eri >= 40 ? 'emerging' : 'early';
  const bandColor = { strong: '#1E6B68', growing: '#2D8B87', emerging: '#4AACA8', early: '#B8906A' }[band];
  const streak  = client.current_streak || 0;
  const alert   = client.consecutive_missed >= 3 || (eri && eri < 30);

  return (
    <div
      onClick={() => onSelect(client)}
      style={{
        background: '#fff', borderRadius: '16px', padding: '18px 20px',
        marginBottom: '12px', cursor: 'pointer',
        border: isSelected ? `1px solid ${bandColor}` : '1px solid rgba(184,211,207,0.3)',
        boxShadow: isSelected ? `0 4px 20px ${bandColor}20` : '0 2px 12px rgba(30,107,104,0.06)',
        transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '14px'
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: '50%',
        background: `${bandColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        <User size={18} color={bandColor} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1E3A39' }}>
            {client.display_name || client.email || 'Client'}
          </span>
          {alert && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Alert size={13} color="#B8906A" />
              <span style={{ fontSize: '0.68rem', color: '#B8906A', fontWeight: '600' }}>Needs attention</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
          <span style={{ fontSize: '0.75rem', color: bandColor, fontWeight: '600' }}>
            ERI {eri ?? '—'}/100
          </span>
          <span style={{ fontSize: '0.75rem', color: '#AAB8B6' }}>
            {streak} day streak
          </span>
          <span style={{ fontSize: '0.75rem', color: '#AAB8B6' }}>
            {client.total_sessions ?? 0} sessions
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Client detail panel ────────────────────────────────────────────────────
function ClientDetail({ client, onClose }) {
  const [report, setReport]   = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote]       = useState('');
  const [savedNote, setSavedNote] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [reportRes, patternsRes] = await Promise.allSettled([
          axios.get(`${API}/intelligence/clinical-report/${client.user_id}?period_days=30`),
          axios.get(`${API}/intelligence/patterns/${client.user_id}`)
        ]);
        if (reportRes.status === 'fulfilled') setReport(reportRes.value.data);
        if (patternsRes.status === 'fulfilled') setPatterns(patternsRes.value.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, [client.user_id]);

  const eri    = client.eri_score;
  const bandColor = eri >= 80 ? '#1E6B68' : eri >= 60 ? '#2D8B87' : eri >= 40 ? '#4AACA8' : '#B8906A';

  return (
    <div style={{
      background: '#fff', borderRadius: '20px', padding: '28px',
      boxShadow: '0 4px 24px rgba(30,107,104,0.1)',
      border: `1px solid ${bandColor}30`
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', color: '#1E3A39' }}>
            {client.display_name || client.email}
          </h2>
          <span style={{ fontSize: '0.78rem', color: '#AAB8B6' }}>
            {client.email} · {client.focus_area || 'no focus area set'}
          </span>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#B8D3CF', fontSize: '1.2rem'
        }}>×</button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'ERI', value: eri ?? '—', unit: '/100' },
          { label: 'Streak', value: client.current_streak ?? 0, unit: 'd' },
          { label: 'Sessions', value: client.total_sessions ?? 0, unit: '' },
          { label: 'Avg Δ', value: report?.stats?.avg_regulation_delta ?? '—', unit: 'pts' }
        ].map(st => (
          <div key={st.label} style={{
            flex: 1, minWidth: 70, textAlign: 'center',
            background: 'rgba(247,243,236,0.8)', borderRadius: '10px', padding: '10px 8px'
          }}>
            <div style={{ fontSize: '0.62rem', color: '#AAB8B6', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.06em', marginBottom: '2px' }}>{st.label}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1E3A39', fontFamily: "'Playfair Display', serif" }}>
              {st.value}{st.unit && <span style={{ fontSize: '0.7rem', color: '#8FA3A0' }}>{st.unit}</span>}
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <p style={{ color: '#AAB8B6', fontSize: '0.85rem', textAlign: 'center', padding: '20px' }}>Loading client data…</p>
      ) : (
        <>
          {/* Patterns */}
          {patterns?.ready && patterns.patterns?.slice(0, 2).map((p, i) => (
            <div key={i} style={{
              background: 'rgba(247,243,236,0.8)', borderRadius: '12px',
              padding: '14px 16px', marginBottom: '10px'
            }}>
              <div style={{ fontSize: '0.68rem', color: '#8FA3A0', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>{p.category}</div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1E3A39', marginBottom: '4px' }}>{p.title}</div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#666', lineHeight: '1.5' }}>{p.insight}</p>
            </div>
          ))}

          {/* Report narrative excerpt */}
          {report?.ready && report?.narrative?.recommendation && (
            <div style={{
              background: 'rgba(30,107,104,0.05)', borderRadius: '12px',
              padding: '14px 16px', marginBottom: '16px',
              borderLeft: '3px solid #1E6B68'
            }}>
              <div style={{ fontSize: '0.68rem', color: '#2D8B87', textTransform: 'uppercase', fontWeight: '700', marginBottom: '6px' }}>Clinical note (auto-generated)</div>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#444', lineHeight: '1.6' }}>{report.narrative.recommendation}</p>
            </div>
          )}
        </>
      )}

      {/* Practitioner note */}
      <div>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#1E6B68', marginBottom: '6px' }}>
          Your session note
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Private note for this client. Not visible to the user."
          rows={3}
          style={{
            width: '100%', padding: '12px', borderRadius: '10px',
            border: '1px solid rgba(184,211,207,0.5)',
            fontSize: '0.88rem', fontFamily: 'inherit', resize: 'vertical',
            background: 'rgba(255,255,255,0.9)'
          }}
        />
        <button
          onClick={() => setSavedNote(true)}
          style={{
            marginTop: '8px', padding: '10px 20px',
            background: savedNote ? 'rgba(30,107,104,0.06)' : 'rgba(30,107,104,0.08)',
            border: '1px solid rgba(30,107,104,0.25)', borderRadius: '12px',
            color: '#1E6B68', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '500'
          }}
        >
          {savedNote ? 'Saved locally ✓' : 'Save note'}
        </button>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function PractitionerPortal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients]     = useState([]);
  const [selected, setSelected]   = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [accessCode, setCode]     = useState('');
  const [codeError, setCodeError] = useState('');
  const [facilitator, setFacilitator] = useState(null);
  const [authenticated, setAuth]  = useState(false);

  // Auto-authenticate if facilitator session exists in localStorage
  useEffect(() => {
    const saved = localStorage.getItem('hyp_facilitator');
    if (saved) {
      try {
        const f = JSON.parse(saved);
        if (f?.access_code) {
          setFacilitator(f);
          setAuth(true);
        }
      } catch {}
    }
    setTimeout(() => setIsVisible(true), 80);
  }, []);

  useEffect(() => {
    if (!authenticated || !facilitator?.access_code) return;
    loadClients(facilitator.access_code);
  }, [authenticated, facilitator]);

  const handleCodeSubmit = async () => {
    if (!accessCode.trim()) return;
    setCodeError('');
    try {
      // Look up facilitator by access code
      const res = await axios.get(`${API}/facilitators/by-code/${accessCode.trim().toUpperCase()}`);
      if (res.data?.access_code) {
        setFacilitator(res.data);
        localStorage.setItem('hyp_facilitator', JSON.stringify(res.data));
        setAuth(true);
      } else {
        setCodeError('Invalid access code. Please check and try again.');
      }
    } catch {
      setCodeError('Invalid access code. Please check and try again.');
    }
  };

  const loadClients = async (code) => {
    try {
      const res = await axios.get(`${API}/practitioner/clients?practitioner_code=${code}`);
      setClients(res.data || []);
    } catch {}
    setIsLoading(false);
  };

  if (!authenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, rgba(184,211,207,0.18) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
        opacity: isVisible ? 1 : 0, transition: 'opacity 0.5s ease'
      }}>
        <div style={{
          background: '#fff', borderRadius: '24px', padding: '40px 36px',
          maxWidth: 420, width: '100%',
          boxShadow: '0 8px 40px rgba(30,107,104,0.12)', textAlign: 'center'
        }}>
          <img src={orbUrl} alt="" style={{ width: 48, marginBottom: 16 }} />
          <img src={wordmarkUrl} alt="HYPSEYA" style={{ height: 14, display: 'block', margin: '0 auto 24px', opacity: 0.7 }} />
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', color: '#1E3A39', margin: '0 0 8px' }}>
            Practitioner Portal
          </h2>
          <p style={{ color: '#8FA3A0', fontSize: '0.85rem', marginBottom: '28px' }}>
            Access is restricted to registered HYPSEYA practitioners. Enter your access code to continue.
          </p>
          <input
            type="text"
            placeholder="e.g. A1B2C3D4"
            value={accessCode}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleCodeSubmit()}
            style={{
              width: '100%', padding: '12px 16px', borderRadius: '12px',
              border: `1px solid ${codeError ? 'rgba(192,57,43,0.4)' : 'rgba(184,211,207,0.6)'}`,
              fontSize: '1rem', marginBottom: '6px', fontFamily: 'monospace',
              textAlign: 'center', letterSpacing: '0.15em', textTransform: 'uppercase',
              boxSizing: 'border-box'
            }}
          />
          {codeError && (
            <p style={{ color: '#C0392B', fontSize: '0.78rem', margin: '0 0 10px', textAlign: 'center' }}>
              {codeError}
            </p>
          )}
          <button
            onClick={handleCodeSubmit}
            style={{
              width: '100%', padding: '14px',
              background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
              color: '#fff', border: 'none', borderRadius: '14px',
              fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer',
              marginTop: codeError ? '0' : '6px'
            }}
          >
            Access Portal
          </button>
          <p style={{ color: '#B8D3CF', fontSize: '0.72rem', marginTop: '12px' }}>
            Don't have a code? <span
              onClick={() => navigate('/facilitator')}
              style={{ color: '#2D8B87', cursor: 'pointer', textDecoration: 'underline' }}
            >Register as a facilitator →</span>
          </p>
          <button onClick={() => navigate(-1)} style={{
            marginTop: '12px', background: 'none', border: 'none',
            color: '#B8D3CF', fontSize: '0.8rem', cursor: 'pointer'
          }}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, rgba(184,211,207,0.18) 100%)',
      padding: '28px 20px 100px', maxWidth: '780px', margin: '0 auto',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease'
    }}>

      <button onClick={() => navigate(-1)} style={{
        display: 'flex', alignItems: 'center', gap: '6px', background: 'none',
        border: 'none', cursor: 'pointer', color: '#8FA3A0', fontSize: '0.82rem', marginBottom: '20px', padding: 0
      }}>
        <ArrowLeft size={14} color="#8FA3A0" /> Back
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={orbUrl} alt="" style={{ width: 28 }} />
          <img src={wordmarkUrl} alt="HYPSEYA" style={{ height: 12, opacity: 0.65 }} />
        </div>
        <div style={{ width: 1, height: 20, background: 'rgba(184,211,207,0.5)' }} />
        <span style={{ fontSize: '0.8rem', color: '#8FA3A0', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: '600' }}>
          Practitioner Portal
        </span>
        {facilitator?.name && (
          <>
            <div style={{ width: 1, height: 20, background: 'rgba(184,211,207,0.5)' }} />
            <span style={{ fontSize: '0.82rem', color: '#1E6B68', fontWeight: '500' }}>
              {facilitator.name}
            </span>
          </>
        )}
        <button
          onClick={() => { localStorage.removeItem('hyp_facilitator'); navigate('/facilitator'); }}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#B8D3CF', fontSize: '0.75rem', cursor: 'pointer' }}
        >
          Sign out
        </button>
      </div>

      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {/* Client list */}
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{
            fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em',
            textTransform: 'uppercase', fontWeight: '600', marginBottom: '14px'
          }}>
            Your clients ({clients.length})
          </div>

          {isLoading ? (
            <p style={{ color: '#AAB8B6', fontSize: '0.85rem' }}>Loading…</p>
          ) : clients.length === 0 ? (
            <div style={{
              background: '#fff', borderRadius: '16px', padding: '32px 24px',
              textAlign: 'center', boxShadow: '0 2px 16px rgba(30,107,104,0.06)'
            }}>
              <User size={32} color="#B8D3CF" style={{ margin: '0 auto 12px' }} />
                      <p style={{ color: '#8FA3A0', fontSize: '0.88rem', margin: 0 }}>
                No clients linked yet. Share your access code with clients to connect.
              </p>
              {facilitator?.access_code && (
                <>
                  <div style={{
                    marginTop: '16px', padding: '10px 16px',
                    background: 'rgba(30,107,104,0.06)', borderRadius: '10px',
                    fontSize: '1rem', color: '#1E6B68', fontWeight: '700',
                    letterSpacing: '0.15em', fontFamily: 'monospace'
                  }}>
                    {facilitator.access_code}
                  </div>
                  <p style={{ color: '#B8D3CF', fontSize: '0.7rem', margin: '6px 0 0', fontStyle: 'italic' }}>
                    your access code — share with clients
                  </p>
                </>
              )}
            </div>
          ) : (
            clients.map(c => (
              <ClientCard
                key={c.user_id}
                client={c}
                onSelect={setSelected}
                isSelected={selected?.user_id === c.user_id}
              />
            ))
          )}
        </div>

        {/* Client detail */}
        {selected && (
          <div style={{ flex: 1, minWidth: 280 }}>
            <ClientDetail client={selected} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>
    </div>
  );
}
